import { api } from "../lib/api";
import { session } from "../stores/session";

export interface PlCategoryAmount {
  /** Null for the accrual basis's synthetic invoiced-turnover line — it comes from
   * invoices, which carry no category. */
  category_id: string | null;
  category_name: string;
  tax_group: string;
  amount: string;
  comparison_amount: string | null;
}

export interface PlEquipmentPurchase {
  transaction_id: string;
  date: string;
  description: string;
  amount: string;
}

export interface PlLoanMemo {
  loan_id: string;
  lender_name: string;
  loan_type: "bank_loan" | "directors_loan_in" | "other";
  outstanding_balance: string;
}

/** Where a VAT figure came from: `payments` = vat_payment transactions that left the
 * bank in the period; `locked_periods` = filed Box 1 totals; `computed` = recomputed from
 * invoices because no filed return covers the period. */
export type VatSource = "payments" | "locked_periods" | "computed";

export interface PlVatToHmrc {
  amount: string;
  source: VatSource;
}

export interface PlFrsSurplus {
  vat_charged: string;
  vat_paid_to_hmrc: string;
  surplus: string;
  source: VatSource;
}

export type PlBasis = "cash" | "accrual";

export interface PlBasisReconciliation {
  invoiced_turnover: string;
  cash_turnover: string;
  difference: string;
  debtors_at_start: string;
  debtors_at_end: string;
  debtors_movement: string;
  reconciles: boolean;
}

export interface CtFinancialYearPortion {
  fy_start_year: number;
  days: number;
  profit: string;
  lower_limit: string;
  upper_limit: string;
  tax: string;
  marginal_relief: string;
}

/** Indicative estimate only — never a filing figure. Any UI showing these numbers must
 * carry that caveat visibly (10-corporation-tax.md). */
export interface PlCorporationTax {
  operating_profit: string;
  capital_allowances: string;
  taxable_total_profits: string;
  associated_companies_count: number;
  days_in_period: number;
  portions: CtFinancialYearPortion[];
  /** Null when the period touches a financial year with no configured rates. */
  tax: string | null;
  marginal_relief: string | null;
  effective_rate: string | null;
  unavailable_reason: string | null;
  salary_hygiene_note: string | null;
}

export interface PlReport {
  basis: PlBasis;
  start_date: string;
  end_date: string;
  comparison_start_date: string | null;
  comparison_end_date: string | null;

  income_lines: PlCategoryAmount[];
  income_total: string;
  income_comparison_total: string | null;

  cogs_lines: PlCategoryAmount[];
  cogs_total: string;
  cogs_comparison_total: string | null;

  gross_profit: string;
  gross_profit_comparison: string | null;

  opex_lines: PlCategoryAmount[];
  opex_total: string;
  opex_comparison_total: string | null;

  loan_interest_total: string;
  loan_interest_comparison_total: string | null;

  vat_to_hmrc: PlVatToHmrc;
  vat_to_hmrc_comparison: string | null;

  operating_profit: string;
  operating_profit_comparison: string | null;

  frs_surplus: PlFrsSurplus;
  /** Accrual basis only — on cash there is no second basis to reconcile against. */
  basis_reconciliation: PlBasisReconciliation | null;
  corporation_tax: PlCorporationTax;

  non_allowable_total: string;
  equipment_purchases: PlEquipmentPurchase[];
  equipment_purchases_total: string;
  owner_drawings_total: string;
  vat_paid_total: string;
  tax_paid_total: string;
  transfers_total: string;
  transfers_count: number;
  loan_principal_repaid_total: string;
  loan_drawdowns_received_total: string;
  loans: PlLoanMemo[];
  uncategorised_count: number;
}

export interface PlReportParams {
  startDate?: string;
  endDate?: string;
  comparison?: boolean;
  basis?: PlBasis;
}

function reportQuery(params: PlReportParams): URLSearchParams {
  const query = new URLSearchParams();
  if (params.startDate) query.set("start_date", params.startDate);
  if (params.endDate) query.set("end_date", params.endDate);
  if (params.comparison !== undefined) query.set("comparison", String(params.comparison));
  if (params.basis) query.set("basis", params.basis);
  return query;
}

export function getPlReport(params: PlReportParams): Promise<PlReport> {
  const query = reportQuery(params);
  const qs = query.toString();
  return api.get<PlReport>(`/api/reports/pl${qs ? `?${qs}` : ""}`);
}

/** Not fetched via the JSON api client — plain browser navigation/download links,
 * same pattern as vatReportCsvUrl/vatReportPdfUrl (services/vat.ts). */
function reportExportUrl(params: PlReportParams, format: "csv" | "pdf"): string {
  const query = reportQuery(params);
  query.set("format", format);
  query.set("company_id", session.companyId.value ?? "");
  return `/api/reports/pl?${query.toString()}`;
}

export function plReportCsvUrl(params: PlReportParams): string {
  return reportExportUrl(params, "csv");
}

export function plReportPdfUrl(params: PlReportParams): string {
  return reportExportUrl(params, "pdf");
}

/** The whole period as one zip for the accountant. Its own route rather than another
 * `format=`, because the package contains the report rather than rendering it — and it
 * carries the same params as the report so the download matches the screen it was taken
 * from, basis included. */
export function plReportPackageUrl(params: PlReportParams): string {
  const query = reportQuery(params);
  query.set("company_id", session.companyId.value ?? "");
  return `/api/reports/pl/package?${query.toString()}`;
}
